<script setup>
import { DocumentTextIcon, DownloadIcon } from '@heroicons/vue/outline'
import BaseButtonMuted from '../../base/BaseButtonMuted.vue'
import { getCampaignData } from '../../../utilities/investment-process/useCampaign'
import { computed } from 'vue'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import { i18n } from '../../../localization'
import { getPlatformName } from '../../../utilities/usePlatformSettings'
import useEventStore from '../../../utilities/useEvent'

const eventStore = useEventStore()

const manualSignature = computed(() => {
    return useInvestmentProcessDataState().createdOrder.emission_signatures.filter(
        (signature) => signature.type === 'manual'
    )
})
const sproofSignature = computed(() => {
    return useInvestmentProcessDataState().createdOrder.emission_signatures.find(
        (signature) => signature.type === 'sproof'
    )
})

const openSproofSignature = async () => {
    await eventStore.trackEvent({
        event: 'signature_process',
        data: {
            provider: 'sproof',
            status: 'started',
        },
        platform_name: getPlatformName(),
    })
    window.open(sproofSignature.value.link, '_blank')
}

const openManualSignature = (signature) => {
    eventStore.trackEvent({
        event: 'signature_process',
        data: {
            provider: 'manual',
            status: 'opened',
        },
        platform_name: getPlatformName(),
    })
    window.open(signature.unsigned_document_link, '_blank')
}

const signatureNotice = computed(() => {
    if (i18n.global.t('investment_process.signature_legal_notice')) {
        return i18n.global.t('investment_process.signature_legal_notice')
    } else {
        return getCampaignData().asset_data.signature_legal_notice
    }
})
</script>

<template>
    <div v-if="sproofSignature || manualSignature" class="overflow-hidden rounded-lg border">
        <div class="px-6 py-4">
            <h3 class="flex text-xl font-bold text-gray-900">
                <DocumentTextIcon v-if="manualSignature.length === 1" class="mr-2 w-5" />
                {{ $t('investment_process.signature_legal_notice_title') }}
            </h3>
            <p class="my-2 font-medium text-gray-500" v-html="signatureNotice"></p>
            <div class="flex items-center">
                <BaseButtonMuted v-if="sproofSignature" size="lg" class="flex-1" @click="openSproofSignature">
                    {{ $t('investment_process.sign_digitally') }}
                </BaseButtonMuted>
                <div v-if="manualSignature && sproofSignature" class="px-4">
                    {{ $t('investment_process.or') }}
                </div>
                <div v-if="manualSignature.length === 1" class="w-full">
                    <BaseButtonMuted
                        v-if="manualSignature"
                        size="lg"
                        class="w-full flex-1"
                        @click="openManualSignature(manualSignature[0])">
                        {{ $t('investment_process.download_signature_document') }}
                    </BaseButtonMuted>
                </div>
                <div
                    v-if="manualSignature.length > 1"
                    class="flex w-full flex-col items-start justify-start space-y-2 rounded-md bg-slate-100 p-2">
                    <div
                        v-for="(signature, index) in manualSignature"
                        :key="signature.unsigned_document_link"
                        class="flex h-10 w-full items-center justify-between rounded-md bg-white px-2 duration-150 hover:bg-slate-50">
                        <div class="flex items-center">
                            <DocumentTextIcon class="mr-2 w-5" />
                            <p class="text-sm text-gray-500">
                                {{ $t(`investment_process.signature_document`) }} {{ index + 1 }}
                            </p>
                        </div>
                        <DownloadIcon class="w-5 cursor-pointer" @click="openManualSignature(signature)" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
